import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useAddCustomItem } from './useAddCustomItem'

const addEssentialSchema = z.object({
  name: z.string().min(1).max(100),
  canonicalUnit: z.string().min(1).max(50),
})

type AddEssentialFormData = z.infer<typeof addEssentialSchema>

interface AddEssentialFormProps {
  onSuccess?: () => void
}

export function AddEssentialForm({ onSuccess }: AddEssentialFormProps) {
  const { t } = useTranslation('pantry')
  const mutation = useAddCustomItem()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddEssentialFormData>({
    resolver: zodResolver(addEssentialSchema),
    mode: 'onBlur',
  })

  const onSubmit = (data: AddEssentialFormData) => {
    mutation.mutate(data, {
      onSuccess: () => {
        reset()
        onSuccess?.()
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="essential-name" className="block text-sm font-medium text-ink mb-1">
          {t('essentials.form.nameLabel')}
        </label>
        <input
          id="essential-name"
          type="text"
          {...register('name')}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder={t('essentials.form.namePlaceholder')}
        />
        {errors.name && (
          <p role="alert" className="mt-1 text-xs text-danger">
            {t('essentials.form.nameRequired')}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="essential-unit" className="block text-sm font-medium text-ink mb-1">
          {t('essentials.form.unitLabel')}
        </label>
        <input
          id="essential-unit"
          type="text"
          {...register('canonicalUnit')}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder={t('essentials.form.unitPlaceholder')}
        />
        {errors.canonicalUnit && (
          <p role="alert" className="mt-1 text-xs text-danger">
            {t('essentials.form.unitRequired')}
          </p>
        )}
      </div>

      {mutation.isError && (
        <p role="alert" className="text-sm text-danger">
          {(mutation.error as Error & { status?: number })?.status === 409
            ? t('essentials.form.duplicateError')
            : t('essentials.form.genericError')}
        </p>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        className="w-full min-h-[44px] bg-accent text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {mutation.isPending ? (
          <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" aria-hidden="true" />
        ) : null}
        {t('essentials.form.submit')}
      </button>
    </form>
  )
}
